import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Flex, Text, Badge, Button, Input, useColorModeValue } from '@chakra-ui/react';
import { createColumnHelper, type ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../services/apiClient';
import DataTable from '../../components/table/DataTable';
import { FilterSidebar, type FilterSection } from '../../components/FilterSidebar';
import { FilterIcon } from '../../components/DataTable';

interface AdminUser {
  id: string;
  email: string;
  fullName: string;
  role: string;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

interface AdminUserPage {
  items: AdminUser[];
  totalCount: number;
  page: number;
  pageSize: number;
}

const columnHelper = createColumnHelper<AdminUser>();

function roleToI18nKey(role: string): string {
  if (role === 'content_curator') return 'contentCurator';
  if (role === 'system_admin') return 'systemAdmin';
  return 'support';
}

function roleBadgeColor(role: string): string {
  if (role === 'system_admin') return 'brand';
  if (role === 'content_curator') return 'blue';
  return 'green';
}

export function AdminUsersListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [pageSize] = useState(20);
  const [roleFilter, setRoleFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const selectedRoles = useMemo(
    () => roleFilter ? roleFilter.split(',').filter(Boolean) : [],
    [roleFilter],
  );
  const selectedActive = useMemo(
    () => activeFilter ? activeFilter.split(',').filter(Boolean) : [],
    [activeFilter],
  );
  const [search, setSearch] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const toggleRole = useCallback((value: string) => {
    const current = roleFilter ? roleFilter.split(',').filter(Boolean) : [];
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    setRoleFilter(next.join(','));
    setPage(0);
  }, [roleFilter]);

  const toggleActive = useCallback((value: string) => {
    const current = activeFilter ? activeFilter.split(',').filter(Boolean) : [];
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    setActiveFilter(next.join(','));
    setPage(0);
  }, [activeFilter]);

  const textColor = useColorModeValue('navy.700', 'white');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-users', page, roleFilter, activeFilter, search],
    queryFn: () => apiClient.get<AdminUserPage>('/admin/admin-users', {
      page: page + 1,
      pageSize,
      ...(roleFilter && { role: roleFilter }),
      ...(activeFilter && { isActive: activeFilter }),
      ...(search && { search }),
    }),
  });

  const columns = [
    columnHelper.accessor('fullName', {
      header: t('admin.users.fullName'),
      cell: (info) => (
        <Text fontSize="sm" fontWeight="600" color={textColor}>
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('email', {
      header: t('admin.users.email'),
      cell: (info) => (
        <Text fontSize="sm" color={textColor}>
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('role', {
      header: t('admin.users.role'),
      cell: (info) => (
        <Badge colorScheme={roleBadgeColor(info.getValue())} borderRadius="8px" px="10px">
          {t(`admin.users.roles.${roleToI18nKey(info.getValue())}`)}
        </Badge>
      ),
    }),
    columnHelper.accessor('isActive', {
      header: t('admin.common.status'),
      cell: (info) => (
        <Badge colorScheme={info.getValue() ? 'green' : 'red'} borderRadius="8px" px="10px">
          {info.getValue() ? t('admin.common.active') : t('admin.common.inactive')}
        </Badge>
      ),
    }),
    columnHelper.accessor('lastLoginAt', {
      header: t('admin.users.lastLogin'),
      cell: (info) => (
        <Text fontSize="sm" color={textColor}>
          {info.getValue() ? new Date(info.getValue()!).toLocaleDateString() : '-'}
        </Text>
      ),
    }),
  ];

  const filterSections = useMemo<FilterSection[]>(() => [
    {
      key: 'role',
      label: t('admin.users.role'),
      options: [
        { value: 'support', label: t('admin.users.roles.support') },
        { value: 'content_curator', label: t('admin.users.roles.contentCurator') },
        { value: 'system_admin', label: t('admin.users.roles.systemAdmin') },
      ],
      selected: selectedRoles,
      onToggle: toggleRole,
    },
    {
      key: 'active',
      label: t('admin.common.status'),
      options: [
        { value: 'true', label: t('admin.common.active') },
        { value: 'false', label: t('admin.common.inactive') },
      ],
      selected: selectedActive,
      onToggle: toggleActive,
    },
  ], [t, selectedRoles, selectedActive, toggleRole, toggleActive]);

  const sidebarFilterCount = selectedRoles.length + selectedActive.length;

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }} data-testid="admin-users-list-page">
      <Flex gap={4}>
        {isSidebarOpen && (
          <FilterSidebar
            sections={filterSections}
            searchInput={search}
            onSearchChange={(value) => { setSearch(value); setPage(0); }}
            onClose={() => setIsSidebarOpen(false)}
            onClearAll={() => { setRoleFilter(''); setActiveFilter(''); setPage(0); }}
          />
        )}

        <Flex direction="column" flex={1}>
          <DataTable<AdminUser>
            headerContent={
              <Flex align="center" gap={3} mb={4}>
                {!isSidebarOpen && (
                  <>
                    <Input
                      placeholder={t('common.search')}
                      value={search}
                      onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                      flex={1}
                      minW={200}
                      size="md"
                      data-testid="search-input"
                      aria-label={t('common.search')}
                    />
                    <Button
                      leftIcon={<FilterIcon />}
                      variant="outline"
                      size="md"
                      borderRadius="10px"
                      onClick={() => setIsSidebarOpen(true)}
                      data-testid="filter-toggle-button"
                    >
                      {t('admin.library.filters.title', 'Filters')}
                      {sidebarFilterCount > 0 && (
                        <Badge ml={2} colorScheme="brand" borderRadius="full" fontSize="xs">
                          {sidebarFilterCount}
                        </Badge>
                      )}
                    </Button>
                  </>
                )}
                <Box ms={isSidebarOpen ? 'auto' : undefined}>
                  <Button
                    variant="brand"
                    onClick={() => navigate('/admin-users/new')}
                    data-testid="btn-create-admin-user"
                  >
                    {t('admin.users.create')}
                  </Button>
                </Box>
              </Flex>
            }
            data={data?.items ?? []}
            columns={columns as ColumnDef<AdminUser>[]}
            totalCount={data?.totalCount}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            isLoading={isLoading}
            onRowClick={(user) => navigate(`/admin-users/${user.id}`)}
            emptyText={t('admin.common.noResults')}
            data-testid="admin-users-table"
          />
        </Flex>
      </Flex>
    </Box>
  );
}
